// src/contexts/CompanyContext.tsx
import React, { createContext, useContext, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CompanyService,
  CompanyProfile,
  CompanySocialMedia,
  CompanyLocation,
} from '../admin/pages/company/companyService';
import { useAuth } from './AuthContext';
import {
  useCompanyProfileQuery,
  useCompanyMutations,
  COMPANY_KEYS,
} from '../hooks/useCompanyProfile';

// ─────────────────────────────────────────────────────────────────────────────
// Context type — IDENTICAL public surface to the original CompanyContext
//
// Every property and method name is preserved exactly so that ALL existing
// consumers (CompanyProfile.tsx and anything else that calls useCompany())
// require zero changes.
// ─────────────────────────────────────────────────────────────────────────────
interface CompanyContextType {
  companyProfile: CompanyProfile | null;
  loading: boolean;
  error: string | null;                                    // preserved
  refreshCompanyProfile: () => Promise<void>;
  updateCompanyProfile: (profile: CompanyProfile) => Promise<void>;
  createCompanyProfile: (profile: CompanyProfile) => Promise<void>;
  hasCompanyProfile: boolean;
  getCompanyLocation: () => CompanyLocation | null;        // preserved
  getCompanySocialMedia: () => CompanySocialMedia | null;  // preserved
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// useCompany — identical export to original
// ─────────────────────────────────────────────────────────────────────────────
export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

// ─────────────────────────────────────────────────────────────────────────────
// CompanyProvider
// ─────────────────────────────────────────────────────────────────────────────
interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  // ── Fetch ─────────────────────────────────────────────────────────────────
  // useCompanyProfileQuery is disabled until a user is authenticated
  // (enabled: !!currentUser).  This mirrors the original useEffect guard:
  //   if (!currentUser) { setLoading(false); return; }
  //
  // `isLoading`  — true only while the very first network request is in-flight
  //                (no cached value exists yet). Replaces manual setLoading(true/false).
  // `isError`    — true when the query threw. Replaces the try/catch + setError().
  // `error`      — the thrown Error object (or null). Cast to string for consumers.
  // `data`       — the resolved CompanyProfile | null | undefined.
  //                undefined = query hasn't run yet (user logged out).
  //                null      = query ran and found no document.
  const {
    data: companyProfile,
    isLoading,
    isError,
    error: queryError,
  } = useCompanyProfileQuery(currentUser?.uid);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const { create, update } = useCompanyMutations();

  // ── Derived state ─────────────────────────────────────────────────────────

  // Normalise undefined (query not yet run / user logged out) → null
  const resolvedProfile = companyProfile ?? null;

  // True once a document was found — mirrors original setHasCompanyProfile logic
  const hasCompanyProfile = resolvedProfile !== null;

  // `loading` — true only on the initial fetch while no cached data exists.
  // After the first successful fetch React Query keeps data populated during
  // background refetches, so consumers never see a stale null flash.
  // When the user is logged out we're not loading, we simply have no data.
  const loading = !!currentUser && isLoading;

  // `error` — string | null, exactly as the original exposed.
  // isError is false when the query is disabled (no user), so error stays null.
  const error: string | null = isError
    ? ((queryError as Error)?.message || 'Failed to load company profile')
    : null;

  // ── refreshCompanyProfile ─────────────────────────────────────────────────
  // Original: called fetchCompanyProfile() which re-ran the async service call.
  // New: invalidates the cache key — React Query re-fetches automatically.
  // Behaviour is identical from the consumer's perspective.
  const refreshCompanyProfile = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: COMPANY_KEYS.all });
  };

  // ── updateCompanyProfile ──────────────────────────────────────────────────
  // Original: called service + then fetchCompanyProfile() to re-sync state.
  // New: mutation's onSuccess invalidates the cache (same net effect).
  // mutateAsync re-throws on failure so the component's catch block still fires.
  const updateCompanyProfile = async (profile: CompanyProfile): Promise<void> => {
    await update.mutateAsync(profile);
  };

  // ── createCompanyProfile ──────────────────────────────────────────────────
  // Original: called service + then fetchCompanyProfile() to re-sync state.
  // New: mutation's onSuccess invalidates the cache.
  // Returns void to match the original signature (document ID is discarded).
  const createCompanyProfile = async (profile: CompanyProfile): Promise<void> => {
    await create.mutateAsync(profile);
  };

  // ── getCompanyLocation ────────────────────────────────────────────────────
  // Unchanged synchronous helper — reads from the cached profile.
  const getCompanyLocation = (): CompanyLocation | null => {
    return resolvedProfile?.location || null;
  };

  // ── getCompanySocialMedia ─────────────────────────────────────────────────
  // Unchanged synchronous helper — reads from the cached profile.
  const getCompanySocialMedia = (): CompanySocialMedia | null => {
    return resolvedProfile?.socialMedia || null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  const value: CompanyContextType = {
    companyProfile: resolvedProfile,
    loading,
    error,
    refreshCompanyProfile,
    updateCompanyProfile,
    createCompanyProfile,
    hasCompanyProfile,
    getCompanyLocation,
    getCompanySocialMedia,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};